import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SettingsService } from '@services/settings.service';
import { NotificationService } from '@services/notification.service';
import { ConfigurableListOption, MaterialOption } from '@models/settings';
import { DEFAULT_EXCHANGE_RATES } from '@classes/vendor-cost';
import {
  DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS,
  DEFAULT_INQUIRY_FOLLOW_UP_HOURS,
  DEFAULT_REPAIR_LOCATIONS,
  DEFAULT_WHO_TO_CALL,
} from '../../../../constants/task-lists';

@Component({
  selector: 'app-duty-settings',
  templateUrl: './duty-settings.component.html',
  styleUrls: ['./duty-settings.component.css'],
})
export class DutySettingsComponent implements OnInit {
  pricingForm: FormGroup;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    private cdRef: ChangeDetectorRef
  ) {
    this.pricingForm = this.fb.group({
      dutyAmount: ['', [Validators.required, Validators.min(0)]],
      nosepadsInstallAmount: ['', [Validators.required, Validators.min(0)]],
      drillMountAmount: ['', [Validators.required, Validators.min(0)]],
      restockingFeePercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
      materials: this.fb.array([]),
      repairLocations: this.fb.array([]),
      whoToCallOptions: this.fb.array([]),
      inquiryFollowUpHours: [
        DEFAULT_INQUIRY_FOLLOW_UP_HOURS,
        [Validators.required, Validators.min(1)],
      ],
      awaitingRxFollowUpHours: [
        DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS,
        [Validators.required, Validators.min(1)],
      ],
      exchangeRates: this.fb.group({
        EUR: [DEFAULT_EXCHANGE_RATES.EUR, [Validators.required, Validators.min(0.000001)]],
        JPY: [DEFAULT_EXCHANGE_RATES.JPY, [Validators.required, Validators.min(0.000001)]],
        CHF: [DEFAULT_EXCHANGE_RATES.CHF, [Validators.required, Validators.min(0.000001)]],
      }),
    });
  }

  get materials(): FormArray {
    return this.pricingForm.get('materials') as FormArray;
  }

  get repairLocations(): FormArray {
    return this.pricingForm.get('repairLocations') as FormArray;
  }

  get whoToCallOptions(): FormArray {
    return this.pricingForm.get('whoToCallOptions') as FormArray;
  }

  ngOnInit() {
    this.settingsService.loadSettings().subscribe({
      next: (settings) => {
        this.applyPricingToForm(settings);
      },
      error: () => {
        this.notificationService.show('error', 'FAILED TO LOAD PRICING SETTINGS');
      },
    });
  }

  createMaterialGroup(option?: Partial<MaterialOption>): FormGroup {
    return this.fb.group({
      label: [option?.label ?? '', [Validators.required]],
      price: [this.formatPriceValue(option?.price), [Validators.required, Validators.min(0)]],
    });
  }

  createListOptionGroup(option?: Partial<ConfigurableListOption>): FormGroup {
    return this.fb.group({
      label: [option?.label ?? '', [Validators.required]],
      active: [option?.active !== false],
    });
  }

  formatMaterialPrice(index: number): void {
    const control = this.materials.at(index)?.get('price');
    if (!control) {
      return;
    }
    control.setValue(this.formatPriceValue(control.value), { emitEvent: false });
  }

  private formatPriceValue(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    const parsed = parseFloat(String(value));
    if (isNaN(parsed)) {
      return '';
    }
    return (Math.round(parsed * 100) / 100).toFixed(2);
  }

  setMaterials(options: MaterialOption[]): void {
    this.materials.clear();
    (options || []).forEach((option) => this.materials.push(this.createMaterialGroup(option)));
    this.cdRef.detectChanges();
  }

  addMaterial(): void {
    this.materials.push(this.createMaterialGroup());
  }

  removeMaterial(index: number): void {
    this.materials.removeAt(index);
  }

  addListOption(controlName: 'repairLocations' | 'whoToCallOptions'): void {
    this.pricingForm.get(controlName) &&
      (this.pricingForm.get(controlName) as FormArray).push(this.createListOptionGroup());
  }

  removeListOption(
    controlName: 'repairLocations' | 'whoToCallOptions',
    index: number
  ): void {
    (this.pricingForm.get(controlName) as FormArray).removeAt(index);
  }

  save() {
    if (this.pricingForm.invalid) {
      this.pricingForm.markAllAsTouched();
      return;
    }

    const dutyAmount = parseFloat(this.pricingForm.value.dutyAmount);
    const nosepadsInstallAmount = parseFloat(this.pricingForm.value.nosepadsInstallAmount);
    const drillMountAmount = parseFloat(this.pricingForm.value.drillMountAmount);
    const restockingFeePercent = parseFloat(this.pricingForm.value.restockingFeePercent);
    const inquiryFollowUpHours = parseFloat(this.pricingForm.value.inquiryFollowUpHours);
    const awaitingRxFollowUpHours = parseFloat(
      this.pricingForm.value.awaitingRxFollowUpHours
    );
    const materials: MaterialOption[] = (this.materials.getRawValue() || []).map(
      (item: { label: string; price: number | string }) => ({
        label: String(item.label || '').trim(),
        price: Math.round(parseFloat(String(item.price)) * 100) / 100,
      })
    );
    const repairLocations = this.listValues(this.repairLocations);
    const whoToCallOptions = this.listValues(this.whoToCallOptions);
    const exchangeRates = this.pricingForm.value.exchangeRates || {};

    if (
      [dutyAmount, nosepadsInstallAmount, drillMountAmount].some(
        (amount) => isNaN(amount) || amount < 0
      ) ||
      isNaN(restockingFeePercent) ||
      restockingFeePercent < 0 ||
      restockingFeePercent > 100 ||
      isNaN(inquiryFollowUpHours) ||
      inquiryFollowUpHours <= 0 ||
      isNaN(awaitingRxFollowUpHours) ||
      awaitingRxFollowUpHours <= 0 ||
      materials.some((item) => !item.label || isNaN(item.price) || item.price < 0) ||
      repairLocations.some((item) => !item.label) ||
      whoToCallOptions.some((item) => !item.label) ||
      ['EUR', 'JPY', 'CHF'].some((code) => {
        const rate = parseFloat(exchangeRates[code]);
        return isNaN(rate) || rate <= 0;
      })
    ) {
      return;
    }

    this.saving = true;
    this.settingsService
      .updatePricingSettings({
        dutyAmount,
        nosepadsInstallAmount,
        drillMountAmount,
        restockingFeePercent,
        materials,
        repairLocations,
        whoToCallOptions,
        inquiryFollowUpHours,
        awaitingRxFollowUpHours,
        exchangeRates: {
          EUR: parseFloat(exchangeRates.EUR),
          JPY: parseFloat(exchangeRates.JPY),
          CHF: parseFloat(exchangeRates.CHF),
        },
      })
      .subscribe({
        next: (settings) => {
          this.saving = false;
          this.applyPricingToForm(settings);
          this.notificationService.show('success', 'PRICING SETTINGS SAVED');
        },
        error: () => {
          this.saving = false;
          this.notificationService.show('error', 'FAILED TO SAVE PRICING SETTINGS');
        },
      });
  }

  private listValues(control: FormArray): ConfigurableListOption[] {
    return (control.getRawValue() || []).map(
      (item: { label: string; active: boolean }) => ({
        label: String(item.label || '').trim(),
        active: item.active !== false,
      })
    );
  }

  private setList(
    control: FormArray,
    options: ConfigurableListOption[],
    fallback: ConfigurableListOption[]
  ): void {
    control.clear();
    const source = options?.length ? options : fallback;
    source.forEach((option) => control.push(this.createListOptionGroup(option)));
  }

  private applyPricingToForm(settings: {
    dutyAmount: number;
    nosepadsInstallAmount: number;
    drillMountAmount: number;
    restockingFeePercent?: number;
    materials?: MaterialOption[];
    repairLocations?: ConfigurableListOption[];
    whoToCallOptions?: ConfigurableListOption[];
    inquiryFollowUpHours?: number;
    awaitingRxFollowUpHours?: number;
    exchangeRates?: { EUR: number; JPY: number; CHF: number };
  }): void {
    this.pricingForm.patchValue({
      dutyAmount: settings.dutyAmount,
      nosepadsInstallAmount: settings.nosepadsInstallAmount,
      drillMountAmount: settings.drillMountAmount,
      restockingFeePercent:
        settings.restockingFeePercent == null ? 15 : settings.restockingFeePercent,
      inquiryFollowUpHours:
        settings.inquiryFollowUpHours || DEFAULT_INQUIRY_FOLLOW_UP_HOURS,
      awaitingRxFollowUpHours:
        settings.awaitingRxFollowUpHours || DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS,
      exchangeRates: {
        EUR: settings.exchangeRates?.EUR || DEFAULT_EXCHANGE_RATES.EUR,
        JPY: settings.exchangeRates?.JPY || DEFAULT_EXCHANGE_RATES.JPY,
        CHF: settings.exchangeRates?.CHF || DEFAULT_EXCHANGE_RATES.CHF,
      },
    });
    this.setMaterials(settings.materials || []);
    this.setList(this.repairLocations, settings.repairLocations || [], DEFAULT_REPAIR_LOCATIONS);
    this.setList(this.whoToCallOptions, settings.whoToCallOptions || [], DEFAULT_WHO_TO_CALL);
  }
}
